/**
 * VIDEO UPLOAD machine (XState v5), Design B / F2-B.
 *
 * Direct browser→R2 presigned multipart flow, RESUMABLE:
 *   selecting → creating → uploading (parts loop) → completing → done
 *                     ↘ failed (RETRY resumes from persisted parts)
 *                     ↘ aborted (ABORT)
 *
 * The machine is PURE: EVERY network call is injected as an actor (`createUpload`,
 * `uploadParts`, `completeUpload`, `abortUpload`) and persistence to IndexedDB
 * as an action (`persist`). This lets it be tested with mocked services, no network or DB.
 *
 * Resumption: `persist` saves `{assetId, uploadId, uploadedParts}`; on reopening,
 * it starts with `input.resume` and `createUpload` in resume mode does `list_parts`
 * to avoid re-uploading what has already been uploaded.
 */

import { assign, fromPromise, setup } from "xstate";
import type { VideoAssetMeta } from "./types";

/** Inert placeholder for an actor's input if the file isn't there yet (this
 * doesn't happen in practice: the START guard requires `file`). Avoids unsafe casts. */
const EMPTY_FILE: UploadFile = { name: "", size: 0, type: "" };

/** A completed multipart part (number + ETag from R2/S3). */
export interface CompletedPart {
  partNumber: number;
  etag: string;
}

/** Metadata for the file to upload (the actual blob is handled by the API layer). */
export interface UploadFile {
  name: string;
  size: number;
  type: string;
  /** Opaque reference to the blob/File (used by the API actors; the machine doesn't touch it). */
  blob?: unknown;
}

/** Resume state persisted in IndexedDB. */
export interface ResumeState {
  assetId: string;
  uploadId: string;
  uploadedParts: CompletedPart[];
}

/** Result of `createUpload` (or its resume mode = list_parts). */
export interface CreateUploadResult {
  assetId: string;
  uploadId: string;
  storageKey: string;
  partSize: number;
  /** Parts already uploaded (empty on fresh creation; populated on resume). */
  uploadedParts: CompletedPart[];
  /** Total number of expected parts. */
  totalParts: number;
}

export interface UploadContext {
  file: UploadFile | null;
  title: string;
  posterKey: string | null;
  durationSeconds: number | null;
  assetId: string | null;
  uploadId: string | null;
  storageKey: string | null;
  partSize: number;
  totalParts: number;
  uploadedParts: CompletedPart[];
  uploadedCount: number;
  asset: VideoAssetMeta | null;
  error: string | null;
  resume: ResumeState | null;
}

export type UploadEvent =
  | {
      type: "SELECT";
      file: UploadFile;
      title?: string;
      posterKey?: string;
      durationSeconds?: number;
    }
  | { type: "START" }
  | { type: "PROGRESS"; uploadedCount: number; uploadedParts: CompletedPart[] }
  | { type: "RETRY" }
  | { type: "ABORT" };

export interface UploadInput {
  file?: UploadFile | null;
  title?: string;
  resume?: ResumeState | null;
}

// ─── Input types for the actors (typed for injection + tests) ─────────────────

export interface CreateUploadInput {
  file: UploadFile;
  title: string;
  resume: ResumeState | null;
}
export interface UploadPartsInput {
  file: UploadFile;
  assetId: string;
  uploadId: string;
  storageKey: string;
  partSize: number;
  totalParts: number;
  uploadedParts: CompletedPart[];
}
export interface CompleteUploadInput {
  assetId: string;
  uploadId: string;
  storageKey: string;
  parts: CompletedPart[];
}
export interface AbortUploadInput {
  assetId: string;
  uploadId: string;
}

export const mediaUploadMachine = setup({
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- XState v5 type-inference pattern
  types: {} as {
    context: UploadContext;
    events: UploadEvent;
    input: UploadInput;
  },
  actors: {
    // Creates the multipart upload (or resumes it via list_parts). Overridden by
    // the API layer / the tests.
    // oxlint-disable-next-line typescript/require-await
    createUpload: fromPromise(
      async ({ input }: { input: CreateUploadInput }): Promise<CreateUploadResult> => {
        throw new Error(`createUpload actor not provided (${input.file.name})`);
      },
    ),
    // Uploads the missing parts (internal loop, presign + PUT). Returns ALL
    // completed parts (the previous ones + the new ones).
    // oxlint-disable-next-line typescript/require-await
    uploadParts: fromPromise(
      async ({ input }: { input: UploadPartsInput }): Promise<CompletedPart[]> => {
        throw new Error(`uploadParts actor not provided (${input.uploadId})`);
      },
    ),
    // Closes the multipart and marks the asset ready (server-side).
    // oxlint-disable-next-line typescript/require-await
    completeUpload: fromPromise(
      async ({ input }: { input: CompleteUploadInput }): Promise<VideoAssetMeta> => {
        throw new Error(`completeUpload actor not provided (${input.assetId})`);
      },
    ),
    // Aborts the multipart (cleans up uploaded parts).
    // oxlint-disable-next-line typescript/require-await
    abortUpload: fromPromise(async ({ input }: { input: AbortUploadInput }): Promise<void> => {
      throw new Error(`abortUpload actor not provided (${input.assetId})`);
    }),
  },
  actions: {
    // Persists {assetId, uploadId, uploadedParts} to IndexedDB. No-op by default
    // (inject it in the UI layer). Must be pure with respect to the machine.
    persist: () => {
      /* override en UI */
    },
    clearPersisted: () => {
      /* override en UI */
    },
  },
}).createMachine({
  id: "mediaUpload",
  context: ({ input }) => ({
    file: input.file ?? null,
    title: input.title ?? input.file?.name ?? "Vídeo",
    posterKey: null,
    durationSeconds: null,
    assetId: input.resume?.assetId ?? null,
    uploadId: input.resume?.uploadId ?? null,
    storageKey: null,
    partSize: 0,
    totalParts: 0,
    uploadedParts: input.resume?.uploadedParts ?? [],
    uploadedCount: input.resume?.uploadedParts.length ?? 0,
    asset: null,
    error: null,
    resume: input.resume ?? null,
  }),
  // If it starts with a file (or resume), it goes straight to selecting; START fires.
  initial: "selecting",
  states: {
    selecting: {
      on: {
        SELECT: {
          actions: assign(({ event }) => ({
            file: event.file,
            title: event.title ?? event.file.name,
            posterKey: event.posterKey ?? null,
            durationSeconds: event.durationSeconds ?? null,
          })),
        },
        START: {
          target: "creating",
          guard: ({ context }) => context.file !== null,
        },
      },
    },

    creating: {
      invoke: {
        src: "createUpload",
        input: ({ context }): CreateUploadInput => ({
          // file cannot be null here (START guard).
          file: context.file ?? EMPTY_FILE,
          title: context.title,
          resume: context.resume,
        }),
        onDone: {
          target: "uploading",
          actions: [
            assign(({ event }) => ({
              assetId: event.output.assetId,
              uploadId: event.output.uploadId,
              storageKey: event.output.storageKey,
              partSize: event.output.partSize,
              totalParts: event.output.totalParts,
              uploadedParts: event.output.uploadedParts,
              uploadedCount: event.output.uploadedParts.length,
              error: null,
            })),
            "persist",
          ],
        },
        onError: {
          target: "failed",
          actions: assign(({ event }) => ({ error: errorMessage(event.error) })),
        },
      },
      on: { ABORT: "aborting" },
    },

    uploading: {
      on: {
        PROGRESS: {
          actions: [
            assign(({ event }) => ({
              uploadedCount: event.uploadedCount,
              uploadedParts: event.uploadedParts,
            })),
            "persist",
          ],
        },
        ABORT: "aborting",
      },
      invoke: {
        src: "uploadParts",
        input: ({ context }): UploadPartsInput => ({
          file: context.file ?? EMPTY_FILE,
          assetId: context.assetId ?? "",
          uploadId: context.uploadId ?? "",
          storageKey: context.storageKey ?? "",
          partSize: context.partSize,
          totalParts: context.totalParts,
          uploadedParts: context.uploadedParts,
        }),
        onDone: {
          target: "completing",
          actions: [
            assign(({ event }) => ({
              uploadedParts: event.output,
              uploadedCount: event.output.length,
              error: null,
            })),
            "persist",
          ],
        },
        onError: {
          target: "failed",
          actions: assign(({ event }) => ({ error: errorMessage(event.error) })),
        },
      },
    },

    completing: {
      invoke: {
        src: "completeUpload",
        input: ({ context }): CompleteUploadInput => ({
          assetId: context.assetId ?? "",
          uploadId: context.uploadId ?? "",
          storageKey: context.storageKey ?? "",
          parts: context.uploadedParts,
        }),
        onDone: {
          target: "done",
          actions: [
            assign(({ event }) => ({ asset: event.output, error: null })),
            "clearPersisted",
          ],
        },
        onError: {
          target: "failed",
          actions: assign(({ event }) => ({ error: errorMessage(event.error) })),
        },
      },
    },

    failed: {
      on: {
        // RETRY resumes: goes back to creating, which in resume mode does list_parts.
        RETRY: {
          target: "creating",
          actions: assign(({ context }) => ({
            resume:
              context.assetId && context.uploadId
                ? {
                    assetId: context.assetId,
                    uploadId: context.uploadId,
                    uploadedParts: context.uploadedParts,
                  }
                : context.resume,
          })),
        },
        ABORT: "aborting",
      },
    },

    aborting: {
      invoke: {
        src: "abortUpload",
        input: ({ context }): AbortUploadInput => ({
          assetId: context.assetId ?? "",
          uploadId: context.uploadId ?? "",
        }),
        // Whether the abort succeeds or fails, we end up in aborted (clearing what was persisted).
        onDone: { target: "aborted", actions: "clearPersisted" },
        onError: { target: "aborted", actions: "clearPersisted" },
      },
    },

    done: { type: "final" },
    aborted: { type: "final" },
  },
});

/** Extracts a readable error message from an `unknown` (XState's onError). */
function errorMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }
  if (typeof err === "string") {
    return err;
  }
  return "upload: error desconocido";
}
